import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';

export function readApiMessage(err: unknown): string | null {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  return data && typeof data === 'object' && 'message' in data
    ? String((data as { message?: string }).message)
    : null;
}

type Options = {
  /** 서버 경로: /inquiry/{path}/send|verify */
  path: 'email-verification' | 'phone-verification';
  /** 요청 본문의 대상 필드 이름 */
  field: 'email' | 'phone';
  /** 입력값 → 비교·전송용 값 */
  normalize: (value: string) => string;
  isValid: (value: string) => boolean;
  invalidMessage: string;
  sentMessage: (target: string) => string;
  showToast: (message: string) => void;
  /** 인증번호 발송 요청에 함께 보낼 값(예: 알림톡 #{고객명}용 성함) */
  extraSendBody?: Record<string, string>;
};

// 인증번호 받기 → 입력 → 확인. 인증번호를 보낸 대상(codeSentTo)과 인증 완료된 대상(verified)을 따로 기억해,
// 입력값이 바뀌면 인증이 자동으로 풀리게 한다.
export function useCodeVerification(rawValue: string, options: Options) {
  const { path, field, normalize, isValid, invalidMessage, sentMessage, showToast, extraSendBody } = options;
  const [codeSentTo, setCodeSentTo] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState('');
  const [token, setToken] = useState('');
  const [notice, setNotice] = useState('');
  const [resendLeft, setResendLeft] = useState(0);

  useEffect(() => {
    if (resendLeft <= 0) return undefined;
    const timer = window.setTimeout(() => setResendLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendLeft]);

  const current = normalize(rawValue);
  const isVerified = !!token && verified === current;
  const codeSentForCurrent = !!codeSentTo && codeSentTo === current;

  const send = async (): Promise<boolean> => {
    if (!isValid(current)) {
      showToast(invalidMessage);
      return false;
    }
    setSending(true);
    try {
      await axios.post(`${MainURL}/inquiry/${path}/send`, { ...extraSendBody, [field]: current });
      setCodeSentTo(current);
      setCode('');
      setVerified('');
      setToken('');
      setResendLeft(60);
      setNotice(sentMessage(current));
      return true;
    } catch (err) {
      showToast(readApiMessage(err) || '인증번호를 보내지 못했습니다. 다시 시도해 주세요.');
      return false;
    } finally {
      setSending(false);
    }
  };

  const verify = async (): Promise<boolean> => {
    if (!/^\d{6}$/.test(code.trim())) {
      showToast('인증번호 6자리를 입력해 주세요.');
      return false;
    }
    setVerifying(true);
    try {
      const res = await axios.post<{ ok: boolean; verificationToken?: string }>(
        `${MainURL}/inquiry/${path}/verify`,
        { [field]: codeSentTo, code: code.trim() }
      );
      if (res.data?.ok && res.data.verificationToken) {
        setVerified(codeSentTo);
        setToken(res.data.verificationToken);
        setNotice('');
        return true;
      }
      return false;
    } catch (err) {
      showToast(readApiMessage(err) || '인증에 실패했습니다. 다시 시도해 주세요.');
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    setVerified('');
    setToken('');
    setCodeSentTo('');
    setCode('');
  };

  return { isVerified, token, codeSentForCurrent, code, setCode, sending, verifying, notice, resendLeft, send, verify, reset };
}

type Verification = ReturnType<typeof useCodeVerification>;

/** 입력칸 오른쪽 버튼: 인증번호 받기 / 재발송 n초 / 변경 */
export function VerifySendButton({ v }: { v: Verification }) {
  if (v.isVerified) {
    return (
      <button type="button" className="email-verify-btn email-verify-btn--ghost" onClick={v.reset}>
        변경
      </button>
    );
  }
  return (
    <button
      type="button"
      className="email-verify-btn"
      onClick={v.send}
      disabled={v.sending || (v.codeSentForCurrent && v.resendLeft > 0)}
    >
      {v.sending
        ? '발송 중...'
        : v.codeSentForCurrent
          ? v.resendLeft > 0
            ? `재발송 ${v.resendLeft}초`
            : '재발송'
          : '인증번호 받기'}
    </button>
  );
}

/** 입력칸 아래: 인증번호 입력·확인, 또는 인증 완료 표시 */
export function VerifyCodePanel({ v, doneMessage }: { v: Verification; doneMessage: string }) {
  if (v.isVerified) {
    return <p className="email-verify-status email-verify-status--ok">{doneMessage}</p>;
  }
  if (!v.codeSentForCurrent) return null;
  return (
    <>
      <div className="email-verify-row email-verify-row--code">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="인증번호 6자리"
          maxLength={6}
          value={v.code}
          onChange={(e) => v.setCode(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              v.verify();
            }
          }}
        />
        <button
          type="button"
          className="email-verify-btn"
          onClick={v.verify}
          disabled={v.verifying || v.code.length !== 6}
        >
          {v.verifying ? '확인 중...' : '확인'}
        </button>
      </div>
      {v.notice && <p className="email-verify-status">{v.notice}</p>}
    </>
  );
}
